import React, { useState, useRef, useEffect, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { Calendar } from 'primereact/calendar';
import { MultiSelect } from 'primereact/multiselect';
import { InputNumber } from 'primereact/inputnumber';
import { Toast } from 'primereact/toast';
import { spaService } from '../services/spaService';
import LoadingSpinner from '../components/LoadingSpinner';

const RideReports = () => {
  const toast = useRef(null);
  const [rideresults, setRideResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [dates, setDates] = useState(null);
  const [expandedRows, setExpandedRows] = useState(null);

  // Load initial data
  useEffect(() => {
    loadAwardsData();
  }, []);

  // Reload data when dates change
  useEffect(() => {
    if (dates && dates[0] && dates[1]) {
      loadAwardsData(dates[0], dates[1]);
    }
  }, [dates]);

  const loadAwardsData = async (fromDate = null, toDate = null) => {
    try {
      setLoading(true);
      setError(null);

      const data = await spaService.getAwardsReport(fromDate, toDate);
      
      if (Array.isArray(data)) {
        // Transform the data to match the expected format
        const transformedData = data.map((item, index) => ({
          id: index + 1,
          riderName: item.riderName,
          ageGroup: item.riderAge,
          horse: item.horse,
          gender: item.gender,
          horseAge: item.horseAge,
          rides: item.totalRides,
          distance: item.totalMileage,
          membership: item.membership,
          joined: new Date(item.joined).getFullYear(),
          rideDetails: item.rides || []
        }));
        
        setRideResults(transformedData);
      } else {
        setError('Unexpected response format from server');
      }
    } catch (err) {
      console.error('Error loading awards data:', err);
      setError(err.message || 'An error occurred while loading awards data');
    } finally {
      setLoading(false);
    }
  };

  // Filter states
  const [filters, setFilters] = useState({
    riderName: { value: null, matchMode: 'contains' },
    ageGroup: { value: null, matchMode: 'in' },
    membership: { value: null, matchMode: 'in' },
    horse: { value: null, matchMode: 'contains' },
    gender: { value: null, matchMode: 'in' },
    horseAge: { value: null, matchMode: 'between' },
    rides: { value: null, matchMode: 'between' },
    distance: { value: null, matchMode: 'between' }
  });

  // Derive filter options dynamically from the loaded data
  const ageGroupOptions = useMemo(() => {
    const unique = [...new Set(rideresults.map(r => r.ageGroup).filter(Boolean))];
    return unique.sort().map(v => ({ label: v, value: v }));
  }, [rideresults]);

  const membershipOptions = useMemo(() => {
    const unique = [...new Set(rideresults.map(r => r.membership).filter(Boolean))];
    return unique.sort().map(v => ({ label: v, value: v }));
  }, [rideresults]);

  const genderOptions = useMemo(() => {
    const unique = [...new Set(rideresults.map(r => r.gender).filter(Boolean))];
    return unique.sort().map(v => ({ label: v, value: v }));
  }, [rideresults]);

  // Custom filter templates
  const multiSelectFilterTemplate = (options, field, choices) => {
    return (
      <MultiSelect
        value={options.value}
        options={choices}
        onChange={(e) => options.filterCallback(e.value)}
        placeholder={`Select ${field}`}
        className="p-column-filter"
        maxSelectedLabels={3}
        style={{ minWidth: '12rem' }}
      />
    );
  };

  const rangeFilterTemplate = (options, field) => {
    return (
      <div className="flex gap-1">
        <InputNumber
          value={options.value ? options.value[0] : null}
          onChange={(e) => {
            const newValue = options.value || [null, null];
            newValue[0] = e.value;
            options.filterCallback(newValue);
          }}
          placeholder="From"
          className="p-column-filter"
          style={{ width: '5rem' }}
        />
        <InputNumber
          value={options.value ? options.value[1] : null}
          onChange={(e) => {
            const newValue = options.value || [null, null];
            newValue[1] = e.value;
            options.filterCallback(newValue);
          }}
          placeholder="To"
          className="p-column-filter"
          style={{ width: '5rem' }}
        />
      </div>
    );
  };

  const distanceBodyTemplate = (rowData) => {
    return `${rowData.distance} miles`;
  };

  const rowExpansionTemplate = (data) => {
    const rides = data.rideDetails || [];
    
    if (rides.length === 0) {
      return (
        <div className="p-3">
          <Message 
            severity="info" 
            text="No ride details available for this rider/horse combination."
            className="w-full"
          />
        </div>
      );
    }

    return (
      <div className="p-3">
        <div className="grid">
          <div className="col-12">
            <div className="se-form-group">
                <DataTable value={rides} >
                    <Column field="eventName" header="Event" />
                    <Column field="date" header="Date" body={x => <span>{new Date(x.date)?.toLocaleDateString() ?? 'Unknown'}</span>} sortable />
                    <Column field="location" header="Location" />
                    <Column 
                      field="mileage" 
                      header="Mileage" 
                      sortable 
                      body={(rowData) => rowData.mileage ? `${rowData.mileage} miles` : '0 miles'}
                    />
                </DataTable>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <Toast ref={toast} />
      <div className="se-card">
        <div className="se-card-header">
          <h2>Award Reports</h2>
          <p className="text-600 mt-2">Generate and view award reports for riders and horses based on performance data.</p>
        </div>

        <div className="se-card-content">
          {loading && <LoadingSpinner message="Loading awards data..." />}
          
          {error && (
            <Message 
              severity="error" 
              text={error}
              className="mb-3 w-full"
            />
          )}
          
          {!loading && !error && (
            <>
              <div className="flex align-items-center gap-3 mb-4">
                <div>
                  <label htmlFor="report-date" className="se-form-label font-semibold">
                    Report Date Range:
                  </label>
                  <div className="flex flex-column gap-1">
                    <Calendar 
                      id="report-date" 
                      value={dates} 
                      onChange={(e) => setDates(e.value)} 
                      selectionMode="range" 
                      readOnlyInput 
                      placeholder="Select date range"
                      className="w-full"
                      dateFormat="dd/mm/yy"
                    />
                    <small className="text-600">
                      {dates && dates[0] && dates[1] 
                        ? `From ${dates[0].toLocaleDateString('en-GB')} to ${dates[1].toLocaleDateString('en-GB')}`
                        : 'Select a date range to filter reports'
                      }
                    </small>
                  </div>
                </div>
                {dates && dates[0] && dates[1] && (
                  <Button
                    label="Clear Dates"
                    icon="pi pi-times"
                    className="p-button-text p-button-sm"
                    onClick={() => {
                      setDates(null);
                      loadAwardsData();
                    }}
                  />
                )}
              </div>

              <DataTable 
                value={rideresults} 
                stripedRows 
                expandedRows={expandedRows}
                onRowToggle={(e) => setExpandedRows(e.data)}
                rowExpansionTemplate={rowExpansionTemplate}
                dataKey="id"
                filters={filters}
                onFilter={(e) => setFilters(e.filters)}
                filterDisplay="menu"
                globalFilterFields={['riderName', 'horse']}
                emptyMessage="No riders found matching the current filters."
                paginator
                rows={10}
                rowsPerPageOptions={[10, 25, 50]}
              >
            <Column expander allowExpansion />
            <Column 
              field="riderName" 
              header="Rider Name" 
              filter 
              filterPlaceholder="Search by name"
              filterMatchMode="contains"
              showFilterMatchModes={false}
              style={{ minWidth: '12rem' }}
            />
            <Column 
              field="ageGroup" 
              header="Rider Age" 
              filter 
              filterElement={(options) => multiSelectFilterTemplate(options, 'Age Groups', ageGroupOptions)}
              filterMatchMode="in"
              showFilterMatchModes={false}
              style={{ minWidth: '12rem' }}
            />
            <Column 
              field="membership" 
              header="Membership" 
              filter 
              filterElement={(options) => multiSelectFilterTemplate(options, 'Memberships', membershipOptions)}
              filterMatchMode="in"
              showFilterMatchModes={false}
              style={{ minWidth: '12rem' }}
            />
            <Column field="joined" header="Joined" sortable />
            <Column 
              field="horse" 
              header="Horse" 
              filter 
              filterPlaceholder="Search by horse name"
              filterMatchMode="contains"
              showFilterMatchModes={false}
              style={{ minWidth: '12rem' }}
            />
            <Column 
              field="gender" 
              header="Gender" 
              filter 
              filterElement={(options) => multiSelectFilterTemplate(options, 'Genders', genderOptions)}
              filterMatchMode="in"
              showFilterMatchModes={false}
              style={{ minWidth: '10rem' }}
            />
            <Column 
              field="horseAge" 
              header="Horse Age" 
              filter 
              filterElement={(options) => rangeFilterTemplate(options, 'Horse Age')}
              filterMatchMode="between"
              showFilterMatchModes={false}
              style={{ minWidth: '12rem' }}
            />
            <Column 
              field="rides" 
              header="Rides" 
              sortable 
              filter 
              filterElement={(options) => rangeFilterTemplate(options, 'Rides')}
              filterMatchMode="between"
              showFilterMatchModes={false}
              style={{ minWidth: '12rem' }}
            />
            <Column 
              field="distance" 
              header="Total Mileage" 
              sortable 
              filter 
              filterElement={(options) => rangeFilterTemplate(options, 'Distance')}
              filterMatchMode="between"
              showFilterMatchModes={false}
              body={distanceBodyTemplate}
              style={{ minWidth: '12rem' }}
            />
          </DataTable>
            </>
          )}
        </div>

      </div>
    </div>
  );
};

export default RideReports;